import { AdmTagMan } from '@components/AppNexus/types'
import { PageMetadata } from '@hooks/usePageMetadata'
import { getCurrentViewportType } from '@measures/responsive'
import { css } from 'styled-components'
import config from '@config'
import { getCookieValue } from '@utils/cookie'

const {
  publication: { publication },
} = config

const adPlaceholderTitle = publication === 'romandie' ? 'Publicité' : 'Anzeige'

const isNotNativeOnlyAd = (item: any) =>
  !item.kind.includes('appnexus') ||
  ['slotCode', 'slotCodeMobile'].some((prop) =>
    Object.keys(item).includes(prop)
  )

const loadSlots = (): void => {
  const admTagMan = window.admTagMan as AdmTagMan
  admTagMan.q.push(function () {
    admTagMan.loadSlots()
  })
}

const refreshSlot = (slotObj: any): void => {
  // @ts-ignore
  const admTagMan = window.admTagMan
  if (admTagMan) {
    admTagMan.q.push(function () {
      ;(admTagMan as any).refreshSlot(slotObj)
    })
  } else {
    console.error('AppNexus: refreshSlot: admTagMan is undefined!')
  }
}

const showSlot = (container: string): void => {
  // @ts-ignore
  const admTagMan = window.admTagMan
  if (admTagMan) {
    admTagMan.q.push(function () {
      ;(admTagMan as any).showSlot(container)
    })
  } else {
    console.error('AppNexus: showSlot: admTagMan is undefined!')
  }
}

const getCurrentAdViewportType = (): 'desktop' | 'mobile' | '' => {
  const currentViewportType = getCurrentViewportType()
  return ['desktop', 'tablet'].includes(currentViewportType)
    ? 'desktop'
    : 'mobile'
}

const getPageType = (type: PageMetadata['type']) => {
  switch (type) {
    case 'section':
      return 'overview'
    case 'article':
      return 'article'
    default:
      return 'overview'
  }
}

const logAdMessage = (...args: any) => {
  if (getCookieValue('ADSDEBUG')) {
    console.log(...args)
  }
}

const globalAdStyles = css`
  :root {
    --adPlaceholderTitle: '${adPlaceholderTitle}';
  }

  /* APPNEXUS_INJECTED_AD_STYLES - DO NOT MODIFY */

  .EverythingWrapper {
    display: block;
    position: relative;
    width: 100%;
    height: 0;
    cursor: pointer;
    -webkit-text-decoration: none;
    text-decoration: none;
    overflow: hidden;

    @media (max-width: 374px) {
      padding-bottom: 150px;
    }

    @media (min-width: 375px) and (max-width: 767px) {
      padding-bottom: 160px;
    }

    @media (min-width: 768px) {
      padding-bottom: 300px;
    }

    .OuterWrapper {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: grid;

      @media (max-width: 767px) {
        grid-template-columns: auto 1fr;
      }

      @media (min-width: 768px) {
        grid-template-rows: auto 1fr;
      }
    }

    .ImageWrapper {
      position: relative;
      min-width: 0;

      @media (max-width: 374px) {
        width: 150px;
        height: 150px;
      }

      @media (min-width: 375px) and (max-width: 767px) {
        width: 160px;
        height: 160px;
      }

      @media (min-width: 768px) {
        width: 100%;
      }
    }

    .Placeholder {
      position: relative;
      width: 100%;
      height: 0;
      background-color: #d5d5d5;

      @media (max-width: 767px) {
        padding-bottom: 100%;
      }

      @media (min-width: 768px) {
        padding-bottom: 56.25%;
      }
    }

    .FancyHolder {
      width: 100%;
      height: 100%;
      position: absolute;
      display: grid;
      -webkit-align-items: center;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      justify-items: center;
      background-color: #dbdbdb;
      overflow: hidden;
    }

    .StyledTriangle1 {
      width: 0;
      height: 0;
      border-top: 500px solid #c3c3c3;
      border-right: 1000px solid transparent;
      border-bottom: 500px solid #c3c3c3;
      position: absolute;
      top: 0;
      left: 0;
    }

    .StyledTriangle2 {
      width: 0;
      height: 0;
      border-top: 500px solid transparent;
      border-right: 1000px solid #dadada;
      border-bottom: 500px solid #dadada;
      position: absolute;
      top: 30%;
      right: 0;
    }

    .StyledTriangle3 {
      width: 0;
      height: 0;
      border-top: 0 solid transparent;
      border-right: 1000px solid #a9a9a9;
      border-bottom: 500px solid transparent;
      position: absolute;
      bottom: 50%;
      right: 0;
    }

    .ResponsiveImageV2 {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .ImageOverlayWrapper {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .PromoIndicator {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .SpecialPromoLine {
      position: absolute;
      box-sizing: border-box;
      display: -webkit-box;
      display: -webkit-flex;
      display: -ms-flexbox;
      display: flex;
      width: 100%;
      top: 8px;
      left: 0;
      padding: 0 8px 0 10px;
      line-height: 0;
    }

    .SpecialPromoLineColorContainer {
      -webkit-flex: 1;
      -ms-flex: 1;
      flex: 1;
    }

    .SpecialPromoLineColorElement {
      width: 100%;
      height: 2px;
      background-color: #fff;
    }

    .SpecialPromoLineLabelContainer {
      padding: 0 10px;
      display: -webkit-box;
      display: -webkit-flex;
      display: -ms-flexbox;
      display: flex;
      -webkit-flex-direction: column;
      -ms-flex-direction: column;
      flex-direction: column;
      -webkit-align-items: flex-end;
      -webkit-box-align: flex-end;
      -ms-flex-align: flex-end;
      align-items: flex-end;

      @media (max-width: 767px) {
        display: none;
      }
    }

    .SpecialPromoLineLabel {
      @media (max-width: 767px) {
        display: none;
      }
      @media (min-width: 768px) {
        font-family: GoodOT;
        text-transform: uppercase;
        font-size: 12px;
        font-weight: 500;
        -webkit-letter-spacing: 2px;
        -moz-letter-spacing: 2px;
        -ms-letter-spacing: 2px;
        letter-spacing: 2px;
        line-height: 1;
        margin-bottom: 5px;
        display: inline-block;
        background-color: rgba(0, 0, 0, 0.8);
        color: #f8f8f8;
        border-radius: 2px;
        padding: 2px 6px;
      }
    }

    .PromoLineRight {
      position: absolute;
      box-sizing: border-box;
      width: 2px;
      height: 100%;
      top: 0;
      right: 8px;
      padding: 8px 0;
    }

    .PromoLineRightColorElement {
      width: 100%;
      height: 100%;
      background-color: #fff;
    }

    .PromoLineBottom {
      position: absolute;
      box-sizing: border-box;
      height: 2px;
      width: 100%;
      bottom: 8px;
      left: 0;
      padding: 0 10px;
    }

    .PromoLineBottomColorElement {
      width: 100%;
      height: 100%;
      background-color: #fff;
    }

    .PromoLineLeft {
      position: absolute;
      box-sizing: border-box;
      width: 2px;
      height: 100%;
      top: 0;
      left: 8px;
      padding: 8px 0;
    }

    .PromoLineLeftColorElement {
      width: 100%;
      height: 100%;
      background-color: #fff;
    }

    .PromoLabel {
      @media (max-width: 767px) {
        font-family: GoodOT;
        text-transform: uppercase;
        font-size: 12px;
        font-weight: 500;
        -webkit-letter-spacing: 2px;
        -moz-letter-spacing: 2px;
        -ms-letter-spacing: 2px;
        letter-spacing: 2px;
        line-height: 1;
        margin-bottom: 5px;
        display: inline-block;
        background-color: #545454;
        color: #f8f8f8;
        border-radius: 2px;
        padding: 2px 6px;
      }

      @media (min-width: 768px) {
        display: none;
      }
    }

    .TextWrapper {
      min-width: 0;
      width: 100%;
      box-sizing: border-box;
      background-color: #f8f8f8;
      line-height: 0;
      text-align: initial;

      @media (max-width: 767px) {
        padding: 6px 6px 6px 10px;
      }

      @media (min-width: 768px) {
        padding: 5px 6px;
      }
    }

    .Catchword {
      font-family: GoodOT;
      font-weight: 500;
      line-height: 1.1;
      color: #545454;

      > strong {
        color: #545454;
        font-style: normal;
        font-weight: 900;
      }

      @media (min-width: 0px) and (max-width: 374px) {
        font-size: 15px;
        margin-bottom: 2px;
      }

      @media (min-width: 375px) and (max-width: 767px) {
        font-size: 17px;
        margin-bottom: 2px;
      }

      @media (min-width: 768px) {
        font-size: 17px;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        word-wrap: normal;
        display: block;
        margin-bottom: 0px;
      }
    }

    .Title {
      font-family: GoodOTNarrow;
      font-weight: 900;
      line-height: 1.1;
      font-style: normal;
      color: #545454;

      > strong {
        color: #545454;
        font-style: normal;
        font-weight: 900;
      }

      @media (min-width: 0px) and (max-width: 374px) {
        font-size: 21px;
        display: -webkit-box;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-wrap: normal;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
      }

      @media (min-width: 375px) and (max-width: 767px) {
        font-size: 24px;
        display: -webkit-box;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-wrap: normal;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
      }

      @media (min-width: 768px) {
        font-size: 28px;
        display: -webkit-box;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-wrap: normal;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
      }
    }

    picture > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`

export {
  isNotNativeOnlyAd,
  loadSlots,
  getPageType,
  refreshSlot,
  showSlot,
  getCurrentAdViewportType,
  logAdMessage,
  globalAdStyles,
}
